import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, type Company } from "../../api/admin";
import { catalogApi, type ProductSummary } from "../../api/catalog";
import { contractsApi, type ContractErpTerms, type ContractLineInput } from "../../api/contracts";
import type { DocumentTemplateOption } from "../../api/documentTemplates";
import { Select } from "../../components/Select";
import type { ApiError } from "../../api/client";
import { useToast } from "../../components/Toast";
import { paymentTermOptions } from "../../lib/erpPaymentTerms";

/** Valute u kojima Bankom radi: na ERP ponudama i porudžbinama iz dve godine pre 04.11.2026 RSD, EUR i USD. */
const VALUTE = ["EUR", "RSD", "USD"];

/** Preuzimanje sa potpisane adrese, bez napuštanja strane. */
function preuzmi(url: string) {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  a.click();
}

/**
 * Unos ugovora, puna stranica.
 *
 * Bio je iskačući prozor sa pet polja, pa je ugovor nastao u portalu bio siromašniji od onog
 * uvezenog iz ERP-a: bez valute, pariteta, roka plaćanja i — što je najvažnije — bez stavki sa
 * cenama. A baš cena i količina su razlog zbog kog se ugovor i otvara.
 *
 * Prozor je zamenjen stranicom jer uslovi i stavke ne staju u njega, a i zato što se ugovor ne unosi
 * u jednom dahu: čovek ga ostavi, ode po podatak, pa se vrati na istu adresu.
 */
export function ContractFormPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const izmena = Boolean(id);

  const [kupac, setKupac] = useState<{ id: string; name: string } | null>(null);
  const [broj, setBroj] = useState("");
  const [naslov, setNaslov] = useState("");
  const [vaziOd, setVaziOd] = useState("");
  const [vaziDo, setVaziDo] = useState("");
  const [potpisan, setPotpisan] = useState("");
  const [valuta, setValuta] = useState("");
  const [paritet, setParitet] = useState("");
  const [mesto, setMesto] = useState("");
  const [placanje, setPlacanje] = useState("");
  const [kontakt, setKontakt] = useState("");
  const [rabat, setRabat] = useState("");
  const [napomena, setNapomena] = useState("");
  const [stavke, setStavke] = useState<ContractLineInput[]>([prazna()]);
  const [cuva, setCuva] = useState(false);
  // Word iz šablona galerije: pravi se posle čuvanja, od sačuvanog ugovora, i ostaje kao njegova verzija.
  const [sabloni, setSabloni] = useState<DocumentTemplateOption[]>([]);
  const [sablon, setSablon] = useState("");
  useEffect(() => {
    contractsApi.templates().then(setSabloni).catch(() => undefined);
  }, []);

  // The incoterm and the payment term go to the ERP, so they are picked from its lists; the payment terms are the
  // customer's, so they come again when the customer changes (an edited contract's once it is loaded).
  const [erpUslovi, setErpUslovi] = useState<ContractErpTerms>({ incoterms: [], paymentTerms: [], deliveryPlaces: [] });
  const kupacId = kupac?.id;
  useEffect(() => {
    if (izmena && !kupacId) return;
    let vazi = true;
    contractsApi.erpTerms(kupacId).then((l) => vazi && setErpUslovi(l)).catch(() => undefined);
    return () => { vazi = false; };
  }, [izmena, kupacId]);

  useEffect(() => {
    if (!id) return;
    contractsApi.get(id)
      .then((c) => {
        setKupac({ id: c.companyId, name: c.companyName });
        setBroj(c.referenceNumber);
        setNaslov(c.title);
        setVaziOd(c.effectiveDate ?? "");
        setVaziDo(c.expiresAt ?? "");
        setPotpisan(c.signedOn ?? "");
        setValuta(c.currency ?? "");
        setParitet(c.incoterm ?? "");
        setMesto(c.incotermPlace ?? "");
        setPlacanje(c.paymentTerms ?? "");
        setKontakt(c.contactPerson ?? "");
        setRabat(c.discountPct == null ? "" : String(c.discountPct));
        setNapomena(c.note ?? "");
        setStavke(c.lines.length === 0 ? [prazna()] : c.lines.map((l) => ({
          productId: l.productId,
          productName: l.productName ?? "",
          quantity: l.quantity,
          unit: l.unit,
          unitPrice: l.unitPrice,
        })));
      })
      .catch((e) => toast.error((e as ApiError).message));
  }, [id]);

  // Cena bez valute nije cena; server to i odbija, pa se kaže ovde a ne posle slanja.
  const imaCenu = stavke.some((s) => s.unitPrice != null);
  const faliValuta = imaCenu && valuta.trim() === "";
  const popunjene = stavke.filter((s) => s.productName.trim() !== "");
  const mozeDaSacuva =
    Boolean(kupac) && broj.trim() !== "" && naslov.trim() !== "" && !faliValuta && !cuva;

  async function sacuvaj() {
    if (!kupac) return;
    setCuva(true);
    const telo = {
      effectiveDate: vaziOd || null,
      expiresAt: vaziDo || null,
      signedOn: potpisan || null,
      currency: valuta.trim().toUpperCase() || null,
      incoterm: paritet.trim() || null,
      incotermPlace: mesto.trim() || null,
      paymentTerms: placanje.trim() || null,
      contactPerson: kontakt.trim() || null,
      note: napomena.trim() || null,
      discountPct: rabat.trim() === "" ? null : Number(rabat),
      lines: popunjene.map((s) => ({ ...s, productName: s.productName.trim() })),
    };
    try {
      const sacuvan = izmena
        ? await contractsApi.update(id!, { ...telo, title: naslov.trim() })
        : await contractsApi.create({
            ...telo,
            companyId: kupac.id,
            referenceNumber: broj.trim(),
            title: naslov.trim(),
          });
      toast.success(t("contractForm.saved", "Ugovor je sačuvan."));
      if (sablon) {
        try {
          const verzija = await contractsApi.versionFromTemplate(sacuvan.id, sablon);
          preuzmi((await contractsApi.presign(sacuvan.id, verzija.id)).url);
        } catch (e) {
          toast.error(t("contractForm.templateFailed", {
            message: (e as ApiError).message,
            defaultValue: "Ugovor je sačuvan, ali Word nije napravljen: {{message}}",
          }));
        }
      }
      navigate(`/admin/contracts?open=${sacuvan.id}`);
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setCuva(false);
    }
  }

  const izmeniStavku = (i: number, izmena: Partial<ContractLineInput>) =>
    setStavke((prev) => prev.map((s, j) => (j === i ? { ...s, ...izmena } : s)));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 1100 }}>
      <header>
        <h1 style={{ margin: 0, fontSize: 20 }}>
          {izmena ? t("contractForm.editTitle", "Izmena ugovora") : t("contractForm.newTitle", "Novi ugovor")}
        </h1>
        <p style={{ margin: "6px 0 0", color: "var(--ink-3)", fontSize: 13, maxWidth: 680 }}>
          {t("contractForm.lead",
             "Uslovi i stavke se unose ovde, a ne dopisuju kasnije: ugovor bez cene i količine ne kaže ono zbog čega se otvara.")}
        </p>
      </header>

      <Odeljak naslov={t("contractForm.whoAndWhat", "Ko i šta")}>
        <div style={mreza}>
          <Polje naslov={t("contractForm.company", "Kupac")} obavezno>
            {izmena ? (
              <div style={{ ...polje, background: "var(--hover)", color: "var(--ink-2)" }}>
                {kupac?.name ?? "—"}
              </div>
            ) : (
              <PretragaFirme izabrana={kupac} onIzbor={setKupac} t={t} />
            )}
          </Polje>
          <Polje naslov={t("contractForm.reference", "Broj ugovora")} obavezno>
            <input value={broj} onChange={(e) => setBroj(e.target.value)} style={polje}
                   disabled={izmena} maxLength={ERP_TEKST} />
          </Polje>
          <Polje naslov={t("contractForm.subject", "Predmet")} obavezno siroko>
            <input value={naslov} onChange={(e) => setNaslov(e.target.value)} style={polje}
                   maxLength={ERP_PREDMET}
                   placeholder={t("contractForm.subjectHint", "npr. Sojina sačma 44% — isporuka 2026")} />
          </Polje>
        </div>
      </Odeljak>

      <Odeljak naslov={t("contractForm.terms", "Uslovi")}
               podnaslov={t("contractForm.termsLead",
                 "Isto što ugovor nosi i kad dođe iz ERP-a. Ništa nije obavezno — ono što se ne zna sada dopisuje se kasnije, a prazno polje je iskrenije od izmišljene vrednosti.")}>
        <div style={mreza}>
          <Polje naslov={t("contractForm.validFrom", "Važi od")}>
            <input type="date" value={vaziOd} onChange={(e) => setVaziOd(e.target.value)} style={polje} />
          </Polje>
          <Polje naslov={t("contractForm.validTo", "Važi do")}>
            <input type="date" value={vaziDo} onChange={(e) => setVaziDo(e.target.value)} style={polje} />
          </Polje>
          <Polje naslov={t("contractForm.signedOn", "Datum potpisivanja")}>
            <input type="date" value={potpisan} onChange={(e) => setPotpisan(e.target.value)} style={polje} />
          </Polje>
          <Polje naslov={t("contractForm.currency", "Valuta")}>
            <Select<string>
              value={valuta}
              onChange={setValuta}
              ariaLabel={t("contractForm.currency", "Valuta")}
              placeholder={t("contractForm.currencyPick", "Izaberite valutu")}
              options={[
                { value: "", label: "—" },
                ...(valuta !== "" && !VALUTE.includes(valuta) ? [{ value: valuta, label: valuta }] : []),
                ...VALUTE.map((v) => ({ value: v, label: v })),
              ]}
            />
            {faliValuta && (
              <small style={{ color: "var(--err)" }}>
                {t("contractForm.currencyRequired", "Stavka ima cenu, pa valuta mora da se unese.")}
              </small>
            )}
          </Polje>
          <Polje naslov={t("contractForm.incoterm", "Paritet")}>
            <Select<string>
              value={paritet}
              onChange={setParitet}
              ariaLabel={t("contractForm.incoterm", "Paritet")}
              placeholder={t("contractForm.incotermPick", "Izaberite paritet iz ERP-a")}
              options={uIzboru(paritet, erpUslovi.incoterms.map((i) => ({ value: i.code, label: `${i.code} — ${i.name}` })),
                t("contractForm.notInErp", "nije sa ERP spiska"))}
            />
          </Polje>
          <Polje naslov={t("contractForm.incotermPlace", "Mesto pariteta")}>
            {/* Places on the ERP's contracts, as the ERP writes them: picking one matches the ERP's own row. */}
            <input value={mesto} onChange={(e) => setMesto(e.target.value)} style={polje} maxLength={ERP_TEKST}
                   list="erp-mesta-pariteta" />
            <datalist id="erp-mesta-pariteta">
              {erpUslovi.deliveryPlaces.map((m) => <option key={m} value={m} />)}
            </datalist>
          </Polje>
          <Polje naslov={t("contractForm.paymentTerms", "Način plaćanja")} siroko>
            <Select<string>
              value={placanje}
              onChange={setPlacanje}
              ariaLabel={t("contractForm.paymentTerms", "Način plaćanja")}
              placeholder={t("contractForm.paymentPick", "Izaberite način plaćanja iz ERP-a")}
              options={paymentTermOptions(placanje, erpUslovi.paymentTerms, {
                mostUsed: t("contractForm.paymentMostUsed", "Najčešće"),
                other: t("contractForm.paymentOther", "Ostali"),
                notInErp: t("contractForm.notInErp", "nije sa ERP spiska"),
              })}
            />
          </Polje>
          <Polje naslov={t("contractForm.contact", "Kontakt osoba")}>
            <input value={kontakt} onChange={(e) => setKontakt(e.target.value)} style={polje} maxLength={ERP_TEKST} />
          </Polje>
          <Polje naslov={t("contractForm.discount", "Rabat %")}>
            <input type="number" min={0} max={100} step="0.01" value={rabat}
                   onChange={(e) => setRabat(e.target.value)} style={polje} />
          </Polje>
          <Polje naslov={t("contractForm.note", "Napomena")} siroko>
            <textarea value={napomena} onChange={(e) => setNapomena(e.target.value)}
                      style={{ ...polje, minHeight: 64, resize: "vertical" }} maxLength={2000} />
          </Polje>
        </div>
      </Odeljak>

      <Odeljak naslov={t("contractForm.lines", "Šta je ugovoreno")}
               podnaslov={t("contractForm.linesLead",
                 "Količina sme da ostane prazna: okvirni ugovor vezuje cenu, a ne obim. Jedinica se uzima sa artikla kad se ne unese.")}>
        <table style={tabela}>
          <thead>
            <tr>
              <th style={th}>{t("contractForm.col.product", "Roba")}</th>
              <th style={{ ...th, width: 130, textAlign: "right" }}>{t("contractForm.col.qty", "Količina")}</th>
              <th style={{ ...th, width: 90 }}>{t("contractForm.col.unit", "Jedinica")}</th>
              <th style={{ ...th, width: 130, textAlign: "right" }}>{t("contractForm.col.price", "Cena")}</th>
              <th style={{ ...th, width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {stavke.map((s, i) => (
              <tr key={i}>
                <td style={td}>
                  <PretragaArtikla
                    ime={s.productName}
                    onIme={(v) => izmeniStavku(i, { productName: v, productId: null })}
                    onIzbor={(p) => izmeniStavku(i, { productId: p.id, productName: p.name })}
                    t={t}
                  />
                </td>
                <td style={td}>
                  <input type="number" min={0} step="0.001" value={s.quantity ?? ""}
                         onChange={(e) => izmeniStavku(i, { quantity: e.target.value === "" ? null : Number(e.target.value) })}
                         style={{ ...polje, textAlign: "right" }} />
                </td>
                <td style={td}>
                  <input value={s.unit ?? ""} onChange={(e) => izmeniStavku(i, { unit: e.target.value })}
                         style={polje} maxLength={20}
                         placeholder={s.productId ? t("contractForm.unitFromProduct", "sa artikla") : ""} />
                </td>
                <td style={td}>
                  <input type="number" min={0} step="0.0001" value={s.unitPrice ?? ""}
                         onChange={(e) => izmeniStavku(i, { unitPrice: e.target.value === "" ? null : Number(e.target.value) })}
                         style={{ ...polje, textAlign: "right" }} />
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  {stavke.length > 1 && (
                    <button type="button" onClick={() => setStavke((p) => p.filter((_, j) => j !== i))}
                            style={ukloni} aria-label={t("contractForm.removeLine", "Ukloni stavku")}>×</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" onClick={() => setStavke((p) => [...p, prazna()])} style={dodaj}>
          {t("contractForm.addLine", "Dodaj stavku")}
        </button>
      </Odeljak>

      <Odeljak naslov={t("contractForm.word", "Ugovor u Wordu")}
               podnaslov={sabloni.length > 0
                 ? t("contractForm.wordLead", "Pri čuvanju portal popunjava izabrani šablon ovim ugovorom i preuzima Word; Word ostaje i kao verzija ugovora.")
                 : t("contractForm.noTemplates", "Šablona još nema: dodaju se u Administracija → Šabloni dokumenata.")}>
        {sabloni.length > 0 && (
          <div style={mreza}>
            <Polje naslov={t("contractForm.template", "Šablon")} siroko>
              <Select<string>
                value={sablon}
                onChange={setSablon}
                ariaLabel={t("contractForm.template", "Šablon")}
                placeholder={t("contractForm.templateNone", "Bez Worda")}
                options={[
                  { value: "", label: t("contractForm.templateNone", "Bez Worda") },
                  ...sabloni.map((x) => ({ value: x.id, label: x.name })),
                ]}
              />
            </Polje>
          </div>
        )}
      </Odeljak>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="button" onClick={() => navigate("/admin/contracts")} style={sporedno}>
          {t("common.cancel", "Otkaži")}
        </button>
        <button type="button" onClick={() => void sacuvaj()} disabled={!mozeDaSacuva} style={glavno}>
          {cuva ? t("common.saving", "Čuva se…") : t("common.save", "Sačuvaj")}
        </button>
      </div>
    </div>
  );
}

/**
 * Biranje firme kucanjem.
 *
 * Padajuća lista je ovde bila neupotrebljiva: nudila je prvu stranu rezultata od trinaest hiljada
 * firmi, poređanu abecedno, pa su na vrhu bila imena koja počinju navodnikom. Pretraga ide u bazu,
 * a ne kroz listu u pregledaču.
 */
function PretragaFirme({
  izabrana, onIzbor, t,
}: {
  izabrana: { id: string; name: string } | null;
  onIzbor: (c: { id: string; name: string }) => void;
  t: (k: string, d: string) => string;
}) {
  const [upit, setUpit] = useState("");
  const [nalazi, setNalazi] = useState<Company[] | null>(null);
  const pauza = useRef<number | undefined>(undefined);

  useEffect(() => {
    window.clearTimeout(pauza.current);
    if (upit.trim().length < 2) { setNalazi(null); return; }
    pauza.current = window.setTimeout(() => {
      // `CUSTOMER` obuhvata i firme označene kao `BOTH`, pa dobavljač koji je i kupac ne ispada.
      adminApi.listCompanies(0, 20, "CUSTOMER", upit.trim())
        .then((p) => setNalazi(p.content.filter(Boolean)))
        .catch(() => setNalazi([]));
    }, 250);
    return () => window.clearTimeout(pauza.current);
  }, [upit]);

  const izaberi = useCallback((c: Company) => {
    onIzbor({ id: c.id, name: c.name });
    setUpit("");
    setNalazi(null);
  }, [onIzbor]);

  if (izabrana) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ ...polje, flex: 1, display: "flex", alignItems: "center" }}>{izabrana.name}</span>
        <button type="button" onClick={() => { setUpit(""); onIzbor({ id: "", name: "" }); }}
                style={sporedno}>{t("contractForm.change", "Promeni")}</button>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <input value={upit} onChange={(e) => setUpit(e.target.value)} style={polje}
             placeholder={t("contractForm.companySearch", "Ukucajte deo naziva kupca…")} />
      {nalazi != null && (
        <ul style={spisak}>
          {nalazi.length === 0 && (
            <li style={{ ...stavkaSpiska, color: "var(--ink-3)" }}>
              {t("contractForm.noCompany", "Nema kupca sa tim nazivom.")}
            </li>
          )}
          {nalazi.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => izaberi(c)} style={dugmeSpiska}>
                <span>{c.name}</span>
                {c.vatNumber && <span style={{ color: "var(--ink-4)", fontSize: 11.5 }}>PIB {c.vatNumber}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Artikal iz kataloga, ali ime sme i slobodno: ugovor ume da vezuje robu koje u katalogu nema. */
function PretragaArtikla({
  ime, onIme, onIzbor, t,
}: {
  ime: string;
  onIme: (v: string) => void;
  onIzbor: (p: ProductSummary) => void;
  t: (k: string, d: string) => string;
}) {
  const [nalazi, setNalazi] = useState<ProductSummary[] | null>(null);
  const pauza = useRef<number | undefined>(undefined);

  useEffect(() => {
    window.clearTimeout(pauza.current);
    if (ime.trim().length < 2) { setNalazi(null); return; }
    pauza.current = window.setTimeout(() => {
      catalogApi.searchProducts(ime.trim(), "sr", 8)
        .then(setNalazi)
        .catch(() => setNalazi(null));
    }, 250);
    return () => window.clearTimeout(pauza.current);
  }, [ime]);

  return (
    <div style={{ position: "relative" }}>
      <input value={ime} onChange={(e) => onIme(e.target.value)} style={polje}
             placeholder={t("contractForm.productSearch", "Naziv robe…")} />
      {nalazi != null && nalazi.length > 0 && (
        <ul style={spisak}>
          {nalazi.map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => { onIzbor(p); setNalazi(null); }} style={dugmeSpiska}>
                <span>{p.name}</span>
                <span style={{ color: "var(--ink-4)", fontSize: 11.5 }}>{p.sku}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** What the ERP holds of these texts: an approved contract goes there as entered. */
const ERP_TEKST = 50;
const ERP_PREDMET = 100;

const prazna = (): ContractLineInput => ({ productId: null, productName: "", quantity: null, unit: null, unitPrice: null });

function Odeljak({ naslov, podnaslov, children }: { naslov: string; podnaslov?: string; children: ReactNode }) {
  return (
    <section style={kartica}>
      <div>
        <h2 style={{ margin: 0, fontSize: 15 }}>{naslov}</h2>
        {podnaslov && (
          <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "var(--ink-3)", maxWidth: 720 }}>{podnaslov}</p>
        )}
      </div>
      {children}
    </section>
  );
}

function Polje({ naslov, obavezno, siroko, children }: {
  naslov: string; obavezno?: boolean; siroko?: boolean; children: ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, gridColumn: siroko ? "1 / -1" : undefined }}>
      <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
        {naslov}{obavezno && <span style={{ color: "var(--err)" }}> *</span>}
      </span>
      {children}
    </label>
  );
}

const kartica: CSSProperties = {
  border: "1px solid var(--border)", borderRadius: "var(--r-md, 10px)", padding: "14px 16px",
  display: "flex", flexDirection: "column", gap: 12, background: "var(--surface)",
};
const mreza: CSSProperties = {
  display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12,
};
const polje: CSSProperties = {
  width: "100%", padding: "8px 10px", fontSize: 13, border: "1px solid var(--border)",
  borderRadius: "var(--r-sm, 8px)", background: "var(--surface)", color: "var(--ink)", boxSizing: "border-box",
};
const spisak: CSSProperties = {
  position: "absolute", zIndex: 20, top: "calc(100% + 4px)", left: 0, right: 0, listStyle: "none",
  margin: 0, padding: 0, background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md, 10px)", boxShadow: "var(--shadow-2)",
  maxHeight: 260, overflowY: "auto",
};
const stavkaSpiska: CSSProperties = { padding: "8px 12px", fontSize: 13 };
const dugmeSpiska: CSSProperties = {
  ...stavkaSpiska, display: "flex", justifyContent: "space-between", gap: 12, width: "100%",
  textAlign: "left", background: "transparent", border: "none", cursor: "pointer", color: "var(--ink)",
};
const tabela: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: CSSProperties = {
  textAlign: "left", padding: "4px 6px", color: "var(--ink-3)", fontWeight: 500, fontSize: 12,
};
const td: CSSProperties = { padding: "4px 6px", verticalAlign: "top" };
const dodaj: CSSProperties = {
  alignSelf: "flex-start", padding: "6px 12px", fontSize: 12.5, background: "transparent",
  border: "1px dashed var(--border)", borderRadius: "var(--r-sm, 8px)", cursor: "pointer", color: "var(--ink-2)",
};
const ukloni: CSSProperties = {
  background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-3)", fontSize: 18, lineHeight: 1,
};
const glavno: CSSProperties = {
  padding: "8px 18px", fontSize: 13, borderRadius: "var(--r-sm, 8px)", border: "none",
  background: "var(--brand-primary)", color: "var(--on-primary)", cursor: "pointer",
};
const sporedno: CSSProperties = {
  padding: "8px 14px", fontSize: 13, borderRadius: "var(--r-sm, 8px)",
  border: "1px solid var(--border)", background: "var(--surface)", color: "var(--ink)", cursor: "pointer",
};

export default ContractFormPage;

/**
 * The options to pick from, with an empty choice first. A value the ERP's list does not hold — an older draft — stays
 * visible, marked, so the user sees what to change before the contract can be approved.
 */
function uIzboru(vrednost: string, opcije: { value: string; label: string }[], oznaka: string) {
  const prazno = { value: "", label: "—" };
  const vanSpiska = vrednost !== "" && !opcije.some((o) => o.value === vrednost)
    ? [{ value: vrednost, label: `${vrednost} (${oznaka})` }]
    : [];
  return [prazno, ...vanSpiska, ...opcije];
}
